import React, { useState, useEffect, FC } from "react";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import { useNavigate, useLocation } from "react-router";
import { formatCurrency } from "../../utils/Utils";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";
import {
  mockWorkOrdersData,
  mockWorkOrderWidgetData,
  mockWorkOrderStatusOptions,
  mockContractorOptions,
  mockContractTypeOptions,
} from "./mockData/viewWorkOrderData";
import { WorkOrderData, WorkOrderStatus } from "./types";
import WorkOrderAside from "./workOrderAside";
import {
  Drawer,
  Button,
  Badge,
  Select,
  Input,
  DatePicker,
  Form,
  Row,
  Col,
  Space,
  Divider,
} from "antd";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterState {
  workOrderNumber?: string;
  workOrderTitle?: string;
  status?: string[];
  siteId?: string[];
  contractorName?: string[];
  contractType?: string;
  createdDateRange?: [any, any] | null;
  releasedDateRange?: [any, any] | null;
  grossValueRange?: { min?: number; max?: number };
  workOrderOwner?: string[];
}

const FinalizedWorkOrders: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sm, setSm] = useState(false);
  const [widgetData, setWidgetData] = useState<any>({});
  const [workOrderStatus, setWorkOrderStatus] = useState<any>([]);
  const [contractorOptions, setContractorOptions] = useState<any>([]);
  const [contractTypeOptions, setContractTypeOptions] = useState<any>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [workOrderOwnerOptions, setWorkOrderOwnerOptions] = useState<any>([]);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [form] = Form.useForm();

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "object" && value !== null) {
          if ("min" in value && value.min !== undefined) count++;
          if ("max" in value && value.max !== undefined) count++;
        } else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // KPI Filter Categories
  const kpiFilterOptions = [
    { label: "By Status", value: "status" },
    { label: "By Value", value: "value" },
    { label: "By Time", value: "time" },
  ];

  const getStatusColor = (
    status: WorkOrderStatus
  ): { color: string; bgColor: string } => {
    switch (status) {
      case WorkOrderStatus.Draft:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
      case WorkOrderStatus.PendingApproval:
        return { color: "#FD7E14", bgColor: "#FFF3E0" };
      case WorkOrderStatus.Rejected:
        return { color: "#DC3545", bgColor: "#FFEBEE" };
      case WorkOrderStatus.Approved:
        return { color: "#28A745", bgColor: "#E8F5E8" };
      case WorkOrderStatus.Released:
        return { color: "#17A2B8", bgColor: "#E0F7FA" };
      case WorkOrderStatus.ContractorConfirmed:
        return { color: "#20C997", bgColor: "#E0F2F1" };
      case WorkOrderStatus.InExecution:
        return { color: "#007BFF", bgColor: "#E3F2FD" };
      case WorkOrderStatus.OnHold:
        return { color: "#FFC107", bgColor: "#FFFDE7" };
      case WorkOrderStatus.VariationPending:
        return { color: "#E83E8C", bgColor: "#FCE4EC" };
      case WorkOrderStatus.Amended:
        return { color: "#6610F2", bgColor: "#F3E5F5" };
      case WorkOrderStatus.Completed:
        return { color: "#198754", bgColor: "#D4EDDA" };
      case WorkOrderStatus.Closed:
        return { color: "#495057", bgColor: "#E9ECEF" };
      case WorkOrderStatus.Cancelled:
        return { color: "#DC3545", bgColor: "#F8D7DA" };
      default:
        return { color: "#6C757D", bgColor: "#F8F9FA" };
    }
  };

  const getProgressPercentage = (record: WorkOrderData): number => {
    if (!record.workOrderLines || record.workOrderLines.length === 0) return 0;

    const totalWeightage = record.workOrderLines.reduce(
      (sum, line) => sum + (line.weightagePercentage || 0),
      0
    );
    const completedWeightage = record.workOrderLines.reduce(
      (sum, line) =>
        sum +
        ((line.completionPercentage || 0) * (line.weightagePercentage || 0)) /
          100,
      0
    );

    return totalWeightage > 0
      ? Math.round((completedWeightage / totalWeightage) * 100)
      : 0;
  };

  const getPaidAmount = (record: any): number => {
    // Mock calculation based on progress for demonstration
    const progress = getProgressPercentage(record);
    const retentionPercent =
      record.paymentConditions?.retentionAmount?.percentageOfRABill || 5;
    const paidAmount =
      ((record.grossValue * progress) / 100) * (1 - retentionPercent / 100);
    return Math.round(paidAmount);
  };

  const getOutstandingAmount = (record: any): number => {
    const paidAmount = getPaidAmount(record);
    const retentionAmount =
      (record.grossValue *
        (record.paymentConditions?.retentionAmount?.percentageOfRABill || 5)) /
      100;
    const onHoldAmount =
      record.paymentConditions?.onHoldAmount?.holdAmountValue || 0;
    return record.grossValue - paidAmount - retentionAmount - onHoldAmount;
  };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 250,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`woStatus-${record?.workOrderId}`}
              onClick={() => {
                const searchParams = new URLSearchParams(location.search);
                const flowType = searchParams.get("flowType");
                const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
                if (record?.status === WorkOrderStatus.Draft) {
                  navigate(
                    `/work-order-creation/?edit=${record?.workOrderId}${
                      flowType ? `&flowType=${flowType}` : ""
                    }`
                  );
                } else {
                  navigate(
                    `/work-order-view/${record.workOrderId}${flowTypeQuery}`
                  );
                }
              }}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={record?.status === WorkOrderStatus.Draft ? "info" : ""}
              id={`woStatus-${record?.id}`}
              direction="right"
              text={
                record?.status === WorkOrderStatus.Draft
                  ? "Click To Continue Work Order Creation"
                  : "Click To View Work Order Details"
              }
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 250,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 30 ? `${text?.substring(0, 27)}... ` : text}
            </span>
            {text?.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site / Project",
      key: "siteProject",
      width: 200,
      render: (text: any, record: any) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName || "N/A"}</div>
        </div>
      ),
    },
    {
      title: isClientFlow ? "Client" : "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 200,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`contractor-tooltip-${index}`}>
              {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`contractor-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Contract Type",
      dataIndex: "contractType",
      key: "contractType",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      // fixed: "right",
      render: (status: WorkOrderStatus) => {
        const statusStyle = getStatusColor(status);
        return (
          <div className="justify-start align-center">
            <span
              className="badge"
              style={{
                color: statusStyle.color,
                backgroundColor: statusStyle.bgColor,
                border: `1px solid ${statusStyle.color}`,
                fontSize: "12px",
                padding: "4px 8px",
              }}
            >
              {status}
            </span>
          </div>
        );
      },
    },
    {
      title: "Created On / By",
      key: "createdInfo",
      width: 180,
      render: (text: any, record: any) => (
        <div>
          <div>{FormatDate(record.createdAt)}</div>
          <div className="text-muted small">{record.createdBy}</div>
        </div>
      ),
    },
    {
      title: "Released On",
      dataIndex: "releasedOn",
      key: "releasedOn",
      width: 180,
      render: (date: any, record: any) => {
        // Mock released date for released and beyond statuses
        const releasedStatuses = [
          WorkOrderStatus.Released,
          WorkOrderStatus.ContractorConfirmed,
          WorkOrderStatus.InExecution,
          WorkOrderStatus.OnHold,
          WorkOrderStatus.Completed,
          WorkOrderStatus.Closed,
        ];
        if (releasedStatuses.includes(record.status)) {
          return <span>{FormatDate(record.createdAt)}</span>;
        }
        return <span>---</span>;
      },
    },
    {
      title: "WO Value (Excl. Tax)",
      dataIndex: "totalValue",
      key: "totalValue",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Tax",
      dataIndex: "taxAmount",
      key: "taxAmount",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Gross Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 180,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span className="fw-bold">{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Paid To Date",
      key: "paidToDate",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency(getPaidAmount(record), "INR")}</span>
        </div>
      ),
    },
    {
      title: "Outstanding",
      key: "outstanding",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>{formatCurrency("25000", "INR")}</span>
        </div>
      ),
    },
    {
      title: "Retention Balance",
      key: "retentionBalance",
      width: 150,
      render: (text: any, record: any) => {
        const retentionAmount =
          (record.grossValue *
            (record.paymentConditions?.retentionAmount?.percentageOfRABill ||
              5)) /
          100;
        return (
          <div style={{ textAlign: "right" }}>
            <span>{formatCurrency(retentionAmount, "INR")}</span>
          </div>
        );
      },
    },
    {
      title: "On-Hold Balance",
      key: "onHoldBalance",
      width: 150,
      render: (text: any, record: any) => (
        <div style={{ textAlign: "right" }}>
          <span>
            {formatCurrency(
              record.paymentConditions?.onHoldAmount?.holdAmountValue || 0,
              "INR"
            )}
          </span>
        </div>
      ),
    },
    {
      title: "Progress %",
      dataIndex: "progressPercent",
      key: "progressPercent",
      width: 120,
      render: (text: any, record: any) => {
        const progress = text;
        return (
          <div style={{ textAlign: "center" }}>
            <span
              className={`badge ${
                progress > 75
                  ? "bg-success"
                  : progress > 50
                  ? "bg-warning"
                  : progress > 25
                  ? "bg-info"
                  : "bg-secondary"
              }`}
            >
              {progress}%
            </span>
          </div>
        );
      },
    },
    // {
    //   title: "Age (Days)",
    //   key: "age",
    //   width: 100,
    //   render: (text: any, record: any) => {
    //     const createdDate = new Date(record.createdAt);
    //     const currentDate = new Date();
    //     const ageInDays = Math.floor(
    //       (currentDate.getTime() - createdDate.getTime()) /
    //         (1000 * 60 * 60 * 24)
    //     );
    //     return (
    //       <div style={{ textAlign: "center" }}>
    //         <span>{ageInDays}</span>
    //       </div>
    //     );
    //   },
    // },
    {
      title: "Owner",
      dataIndex: "workOrderOwner",
      key: "workOrderOwner",
      width: 150,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (date: any, record: any) => (
        <span>{FormatDate(date || record.createdAt)}</span>
      ),
    },
  ];

  useEffect(() => {
    // Load mock data
    setWidgetData(mockWorkOrderWidgetData);
    // Set status options
    const statusOptions = [
      { label: "Draft", value: "Draft" },
      { label: "Submitted", value: "Submitted" },
      { label: "Pending Approval", value: "Pending Approval" },
      { label: "Released", value: "Released" },
      { label: "Approved", value: "Approved" },
      { label: "Rejected", value: "Rejected" },
      { label: "Contractor Confirmed", value: "Contractor Confirmed" },
      { label: "In Execution", value: "In Execution" },
      { label: "On Hold", value: "On Hold" },
      { label: "Variation Pending", value: "Variation Pending" },
      { label: "Amended", value: "Amended" },
      { label: "Completed", value: "Completed" },
      {
        label: "Pending Execution Approval",
        value: "Pending Execution Approval",
      },
      { label: "Cancelled", value: "Cancelled" },
      { label: "Closed", value: "Closed" },
      { label: "Changes Requested", value: "Changes Requested" },
      {
        label: "Pending Contractor Response",
        value: "Pending Contractor Response",
      },
      {
        label: "Changes Under Negotiation",
        value: "Changes Under Negotiation",
      },
      { label: "In Progress", value: "In Progress" },
      { label: "Contractor Rejected", value: "Contractor Rejected" },
    ];
    setWorkOrderStatus(statusOptions);
    setContractTypeOptions(mockContractTypeOptions);
  }, []);

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // KPI Filter Change Handler
  const handleKPIFilterChange = (filterValue: string) => {
    setSelectedKPIFilter(filterValue);
  };

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...filters });
    form.setFieldsValue(filters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const partyType = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || item.contractorName || "N/A",
          value: item.contractorID?.toString() || item.contractorId?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch users based on current filter siteIds
      const siteIds =
        filters.siteId && filters.siteId.length > 0
          ? filters.siteId.join(",")
          : undefined;

      const usersResponse = await dropdownApis.fetchUserDropdown(siteIds);
      if (usersResponse && usersResponse.status && usersResponse.data) {
        const userOptions = usersResponse.data.map((item: any) => ({
          label: item.userName || "N/A",
          value: item.appUserId?.toString() || item.userId?.toString(),
        }));
        setWorkOrderOwnerOptions(userOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    form.resetFields();
  };

  const handleApplyFilters = () => {
    form
      .validateFields()
      .then((values) => {
        // Process form values to match our filter state structure
        const processedFilters: FilterState = {
          workOrderNumber: values.workOrderNumber?.trim() || undefined,
          workOrderTitle: values.workOrderTitle?.trim() || undefined,
          status:
            values.status && values.status.length > 0
              ? values.status
              : undefined,
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          contractType: values.contractType?.trim() || undefined,
          createdDateRange: values.createdDateRange || undefined,
          releasedDateRange: values.releasedDateRange || undefined,
          grossValueRange: {
            min: values.grossValueMin || undefined,
            max: values.grossValueMax || undefined,
          },
          workOrderOwner:
            values.workOrderOwner && values.workOrderOwner.length > 0
              ? values.workOrderOwner
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Remove empty grossValueRange
        if (
          processedFilters.grossValueRange &&
          !processedFilters.grossValueRange.min &&
          !processedFilters.grossValueRange.max
        ) {
          delete processedFilters.grossValueRange;
        }

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    form.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      form.setFieldsValue(savedFilter);
    }
  };

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="Finalized-view-work-order"
        title="Finalized Work Orders"
        columns={columns}
        customFilters={filters}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        extra={{
          tableScrollX: 2500,
          dateRange: false,
          excelExport: true,
          dataFilterTootip: "Filtering based on Work Order created date",
        }}
      />

      <Drawer
        title="Advanced Filters"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={filters}
          onValuesChange={(changedValues, allValues) => {
            // When siteId changes, fetch users for those sites
            if (changedValues.siteId !== undefined) {
              const fetchUsers = async () => {
                try {
                  const siteIds =
                    allValues.siteId && allValues.siteId.length > 0
                      ? allValues.siteId.join(",")
                      : undefined;

                  const response = await dropdownApis.fetchUserDropdown(
                    siteIds
                  );
                  if (response && response.status && response.data) {
                    const options = response.data.map((item: any) => ({
                      label: item.userName || "N/A",
                      value:
                        item.appUserId?.toString() || item.userId?.toString(),
                    }));
                    setWorkOrderOwnerOptions(options);
                    // Clear workOrderOwner if it was selected, as users may have changed
                    if (changedValues.siteId) {
                      form.setFieldsValue({ workOrderOwner: undefined });
                    }
                  }
                } catch (error) {
                  console.error("Error fetching work order owners:", error);
                }
              };
              fetchUsers();
            }
          }}
        >
          <Row gutter={[16, 16]}>
            {/* <Col span={24}>
              <Form.Item name="workOrderNumber" label="Work Order Number">
                <Input
                  placeholder="Enter WO Number"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderTitle" label="Work Order Title">
                <Input
                  placeholder="Enter WO Title"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col> */}

            <Col span={24}>
              <Form.Item name="siteId" label="Site">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractType" label="Contract Type">
                <Select
                  placeholder="Select Contract Type"
                  options={contractTypeOptions}
                  allowClear
                  showSearch
                  filterOption={(input: any, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderOwner" label="Work Order Owner">
                <Select
                  mode="multiple"
                  placeholder="Select Work Order Owners"
                  options={workOrderOwnerOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="status" label="Status">
                <Select
                  mode="multiple"
                  placeholder="Select Status"
                  options={workOrderStatus}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Date Ranges
              </Divider>
            </Col>

            <Col span={24}>
              <Form.Item name="createdDateRange" label="Created Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="releasedDateRange" label="Released Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Value Range
              </Divider>
            </Col>

            <Col span={12}>
              <Form.Item name="grossValueMin" label="Min Gross Value">
                <Input
                  type="number"
                  placeholder="Minimum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="grossValueMax" label="Max Gross Value">
                <Input
                  type="number"
                  placeholder="Maximum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col> */}
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default FinalizedWorkOrders;
